import { ArrowRight, Check, ImageIcon, MessageSquare, Plus, Send, Target, UsersRound, Wallet } from 'lucide-react'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import CreateCampaignPopup from '../../Components/Communications/Campaigns/CreateCampaignPopup'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './ComHome.module.css'
import '../../Components/Communications/Campaigns/shared/campaignConsole.css'

const fmtAmount = (v) => `₹${Math.abs(Number(v) || 0).toLocaleString('en-IN')}`

const fmtDate = (v) => {
    if (!v) return '—'
    const d = new Date(String(v).replace(' ', 'T'))
    if (isNaN(d)) return v
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const toStatus = (c) => {
    if (c?.hasDeleted === '1' || c?.hasDeleted === 1) return 'Deleted'
    if (c?.isActive === '1' || c?.isActive === 1 || c?.status === 'Active') return 'Active'
    if (c?.isActive === '0' || c?.isActive === 0 || c?.status === 'Inactive') return 'Inactive'
    return ''
}

const ONBOARDING_STEPS = [
    { icon: UsersRound, label: 'Choose who to message', desc: "Pick a list of customers — like everyone who hasn't visited in 30 days." },
    { icon: MessageSquare, label: 'Write your message', desc: 'Use a ready-made template, or write your own. No design skills needed.' },
    { icon: Send, label: 'Send it', desc: 'Your message reaches every customer on the list within minutes.' },
]

const ONBOARDING_BENEFITS = [
    'Bring back customers who have stopped visiting',
    'Fill up empty appointment slots on slow days',
    'Tell everyone about a new offer in one shot',
]

const QUICK_ACTIONS = [
    { key: 'all-campaigns', label: 'Campaigns', desc: 'See and manage all your campaigns', icon: Target, to: '/communications/all-campaigns' },
    { key: 'audiences', label: 'Audiences', desc: 'Make lists of customers to message', icon: UsersRound, to: '/communications/audience/all-audiences' },
    { key: 'wallet', label: 'Store wallet', desc: 'See your balance and past payments', icon: Wallet, to: '/communications/store-wallet' },
    // { key: 'creative', label: 'Creative Studio', desc: 'Design pictures for your campaigns', icon: Sparkles, to: '/communications/creative-studio' },
    // { key: 'whatsapp', label: 'WhatsApp number', desc: 'Connect your WhatsApp number', icon: Smartphone, to: '/communications/register-whatsapp-number' },
    // { key: 'send', label: 'Send promo', desc: 'Send a quick message to customers', icon: Send, to: '/communications/send' },
]

const ComHome = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = (() => {
        try { return (asCtx?.activeStore && asCtx.activeStore.trim() !== "") ? JSON.parse(asCtx.activeStore) : null }
        catch { return null }
    })()
    const nav = useNavigate()

    const [isPopUpOpen, setIsPopUpOpen] = useState(false)
    const [campaigns, setCampaigns] = useState([])
    const [campaignsLoading, setCampaignsLoading] = useState(true)
    const [audienceCount, setAudienceCount] = useState(null)
    const [walletSummary, setWalletSummary] = useState(null)

    const campaignCreationHandler = (k) => {
        toast.success('Campaign successfully created!')
        nav(`/communications/campaign/${k}/details`)
    }

    const loadCampaigns = async () => {
        setCampaignsLoading(true)
        try {
            const res = await fetch(BASE_URL_API + 'getAllCampaigns', {
                method: 'POST',
                body: JSON.stringify({ token: authCtx.token, storeIdentifier: activeStore?.storeIdentifier })
            })
            if (!res.ok) return
            const data = await res.json()
            if (data?.status === 'success') {
                setCampaigns(data?.response || [])
            } else if (data?.message === 'tokenExpired') {
                authCtx.logout()
            }
        } catch { /* silent */ } finally { setCampaignsLoading(false) }
    }

    const loadAudienceCount = async () => {
        try {
            const res = await fetch(BASE_URL_API + 'getAllAudiences', {
                method: 'POST',
                body: JSON.stringify({ token: authCtx.token, storeIdentifier: activeStore?.storeIdentifier })
            })
            if (!res.ok) return
            const data = await res.json()
            if (data?.status === 'success') {
                setAudienceCount((data?.response || []).length)
            } else if (data?.message === 'tokenExpired') {
                authCtx.logout()
            }
        } catch { /* silent */ }
    }

    const loadWalletSummary = async () => {
        try {
            const res = await fetch(BASE_URL_API + 'getStoreWalletTransactions', {
                method: 'POST',
                body: JSON.stringify({ token: authCtx.token, storeIdentifier: activeStore?.storeIdentifier, page: 1, limit: 1 })
            })
            if (!res.ok) return
            const data = await res.json()
            if (data?.status === 'success') {
                setWalletSummary(data?.response?.summary ?? { currentBalance: 0, availableBalance: 0, heldAmount: 0 })
            } else if (data?.message === 'tokenExpired') {
                authCtx.logout()
            }
        } catch { /* silent */ }
    }

    useEffect(() => {
        loadCampaigns()
        loadAudienceCount()
        loadWalletSummary()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    // Top-left placement (layout:fixedBottomBar) overlapped this page's own
    // header/title, so the FAB is hidden entirely here instead — same
    // convention as the campaign wizard.
    useEffect(() => {
        window.dispatchEvent(new CustomEvent('layout:hideFab', { detail: { active: true } }))
        return () => {
            window.dispatchEvent(new CustomEvent('layout:hideFab', { detail: { active: false } }))
        }
    }, [])

    const activeCampaignsCount = campaigns.filter(c => toStatus(c) === 'Active').length
    const recentCampaigns = [...campaigns]
        .sort((a, b) => {
            const pa = Date.parse((a?.createdOn || '').replace(' ', 'T'))
            const pb = Date.parse((b?.createdOn || '').replace(' ', 'T'))
            return (isNaN(pb) ? 0 : pb) - (isNaN(pa) ? 0 : pa)
        })
        .slice(0, 5)

    return (
        <div className={`${styles.wrapper} ccScope`}>
            {isPopUpOpen && <CreateCampaignPopup isOpen={isPopUpOpen} onClose={() => setIsPopUpOpen(false)} onSuccess={campaignCreationHandler} />}

            {/* ── Page Header ── */}
            <div className={styles.pageHeader}>
                <div className={styles.pageHeaderLeft}>
                    <div className={styles.statusPill}>Communications</div>
                    <h1 className={styles.pageTitle}>{activeStore?.storeName ? `Welcome back, ${activeStore.storeName}` : 'Communications hub'}</h1>
                    <p className={styles.pageSubtitle}>Send messages to your customers, group them into lists, and manage your wallet — all in one place.</p>
                </div>
                <button className={`${styles.primaryBtn} ${styles.hideOnMobile}`} onClick={() => setIsPopUpOpen(true)} type="button">
                    <Plus size={15} /> Create campaign
                </button>
            </div>

            {/* ── Stat tiles ── */}
            <div className={styles.statRow}>
                <button className={`ccStatTile ${styles.statTileClickable}`} onClick={() => nav('/communications/all-campaigns')} type="button">
                    <div className="ccStatTop">
                        <span className="ccStatIcon" style={{ background: '#ede9fe', color: '#6d28d9' }}><Target size={12} /></span>
                        <span className="ccStatLabel">Active campaigns</span>
                    </div>
                    <div className="ccStatValue" style={{ color: '#6d28d9' }}>{campaignsLoading ? '—' : activeCampaignsCount}</div>
                    <div className="ccStatDelta">{campaignsLoading ? 'Loading…' : `${campaigns.length} total campaign${campaigns.length !== 1 ? 's' : ''}`}</div>
                </button>
                <button className={`ccStatTile ${styles.statTileClickable}`} onClick={() => nav('/communications/audience/all-audiences')} type="button">
                    <div className="ccStatTop">
                        <span className="ccStatIcon" style={{ background: '#dcfce7', color: '#16a34a' }}><UsersRound size={12} /></span>
                        <span className="ccStatLabel">Audiences</span>
                    </div>
                    <div className="ccStatValue" style={{ color: '#16a34a' }}>{audienceCount === null ? '—' : audienceCount}</div>
                    <div className="ccStatDelta">Lists of customers you can message</div>
                </button>
                <button className={`ccStatTile ${styles.statTileClickable}`} onClick={() => nav('/communications/store-wallet')} type="button">
                    <div className="ccStatTop">
                        <span className="ccStatIcon" style={{ background: '#fef3c7', color: '#b45309' }}><Wallet size={12} /></span>
                        <span className="ccStatLabel">Wallet balance</span>
                    </div>
                    <div className="ccStatValue" style={{ color: '#b45309' }}>{walletSummary === null ? '—' : fmtAmount(walletSummary.availableBalance)}</div>
                    <div className="ccStatDelta">Available to spend right now</div>
                </button>
            </div>

            {/* ── Quick actions ── */}
            <div className={styles.sectionLabel}>Quick actions</div>
            <div className={styles.actionsGrid}>
                {QUICK_ACTIONS.map(a => (
                    <button key={a.key} className={styles.actionCard} onClick={() => nav(a.to)} type="button">
                        <span className={styles.actionIcon}><a.icon size={15} /></span>
                        <span className={styles.actionText}>
                            <span className={styles.actionLabel}>{a.label}</span>
                            <span className={styles.actionDesc}>{a.desc}</span>
                        </span>
                        <ArrowRight size={14} className={styles.actionArrow} />
                    </button>
                ))}
            </div>

            {/* ── Recent campaigns / first-campaign onboarding ── */}
            {campaignsLoading ? (
                <div className={styles.tableCard}>
                    <div className={styles.tableTop}>
                        <span className={styles.sectionTitle}>Recent campaigns</span>
                        <button className={styles.viewAllBtn} onClick={() => nav('/communications/all-campaigns')} type="button">
                            View all <ArrowRight size={12} />
                        </button>
                    </div>
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className={styles.rowSkeleton}>
                            <div className={styles.skeletonBar} style={{ width: '55%', height: 13 }} />
                            <div className={styles.skeletonBar} style={{ width: 60, height: 20 }} />
                        </div>
                    ))}
                </div>
            ) : campaigns.length === 0 ? (
                <div className={styles.onboardCard}>
                    <div className={styles.onboardIntro}>
                        <p className={styles.onboardTitle}>Turn your customer list into repeat bookings</p>
                        <p className={styles.onboardSubtitle}>
                            A campaign is just a WhatsApp or SMS message sent to many customers at once. It takes less than 2 minutes to set up.
                        </p>
                    </div>

                    <div className={styles.onboardSteps}>
                        {ONBOARDING_STEPS.map((s, i) => (
                            <div key={s.label} className={styles.onboardStep}>
                                <div className={styles.onboardStepTop}>
                                    <span className={styles.onboardStepNum}>{i + 1}</span>
                                    <span className={styles.onboardStepIcon}><s.icon size={14} /></span>
                                </div>
                                <p className={styles.onboardStepLabel}>{s.label}</p>
                                <p className={styles.onboardStepDesc}>{s.desc}</p>
                            </div>
                        ))}
                    </div>

                    <div className={styles.onboardBenefits}>
                        {ONBOARDING_BENEFITS.map(b => (
                            <div key={b} className={styles.onboardBenefit}>
                                <span className={styles.onboardBenefitCheck}><Check size={12} /></span>
                                {b}
                            </div>
                        ))}
                    </div>

                    <button className={styles.onboardCta} onClick={() => setIsPopUpOpen(true)} type="button">
                        <Plus size={15} /> Create your first campaign
                    </button>
                </div>
            ) : (
                <div className={styles.tableCard}>
                    <div className={styles.tableTop}>
                        <span className={styles.sectionTitle}>Recent campaigns</span>
                        <button className={styles.viewAllBtn} onClick={() => nav('/communications/all-campaigns')} type="button">
                            View all <ArrowRight size={12} />
                        </button>
                    </div>
                    {recentCampaigns.map((c, i) => {
                        const status = toStatus(c)
                        const id = c?.campaignIdentifier || c?.ID || c?.id || c?.identifier || `campaign-${i}`
                        const chipClass = status === 'Active' ? 'ccChipGood' : status === 'Deleted' ? 'ccChipDanger' : 'ccChipNeutral'
                        return (
                            <div key={id} className={styles.campaignRow} onClick={() => nav(`/communications/campaign/${id}/details`)}>
                                <div className={styles.campaignRowLeft}>
                                    <span className={styles.campaignIcon}><ImageIcon size={13} /></span>
                                    <div className={styles.campaignInfo}>
                                        <span className={styles.campaignName}>{c?.campaignName || c?.name || 'Untitled Campaign'}</span>
                                        <span className={styles.campaignMeta}>{c?.templateName || 'No template'} · {fmtDate(c?.createdOn)}</span>
                                    </div>
                                </div>
                                <span className={`ccChip ${chipClass}`}>{status || '—'}</span>
                            </div>
                        )
                    })}
                </div>
            )}

            {/* ── Mobile fixed bottom bar ── */}
            <div className={styles.mobileBottomBar}>
                <div className={styles.mobileBottomBarInner}> 
                    <button className={styles.mobileBottomBarBtn} onClick={() => setIsPopUpOpen(true)} type="button">
                        <Plus size={16} /> Create campaign
                    </button>
                </div>
            </div>
        </div>
    )
}

export default ComHome
